import { useEffect, useMemo, useRef, useState } from 'react'

/* ===========================================================================
   El cordón de tres dobleces.

   "Cordón de tres dobleces no se rompe pronto" (Eclesiastés 4:12) es el rito
   central de la boda cristiana latinoamericana: dos personas y Dios, trenzados.

   Se usa en dos sitios y en ninguno más:
   - CordSpine: columna vertebral de la página pública. Se traza al hacer scroll.
   - CordBraid: progreso de la guía de planificación. Las tres hebras son las
     tres secciones (espiritual, legal, logística); se trenzan al completarse.
   =========================================================================== */

/** Las tres hebras, repartidas a un tercio de vuelta cada una. */
export const FASES = [0, (2 * Math.PI) / 3, (4 * Math.PI) / 3]

export interface HebraOpciones {
  largo: number
  ancho: number
  vueltas: number
  fase: number
  vertical: boolean
  pasos?: number
}

/**
 * Los puntos de una hebra como onda senoidal a lo largo de un eje.
 *
 * Se exporta porque la página de la propuesta dibuja el mismo cordón sobre un
 * lienzo (canvas) para el recuerdo que ella se lleva: esa matemática no puede
 * divergir de la del SVG, o serían dos cordones distintos.
 */
export function puntosDeHebra(opts: HebraOpciones): Array<[number, number]> {
  const { largo, ancho, vueltas, fase, vertical, pasos = 120 } = opts
  const centro = ancho / 2
  const amplitud = ancho / 2 - 1

  const puntos: Array<[number, number]> = []
  for (let i = 0; i <= pasos; i++) {
    const t = i / pasos
    const desvio = centro + amplitud * Math.sin(2 * Math.PI * vueltas * t + fase)
    const avance = largo * t
    puntos.push(vertical ? [desvio, avance] : [avance, desvio])
  }

  return puntos
}

/** Genera el path de una hebra como onda senoidal a lo largo de un eje. */
function hebra(opts: HebraOpciones): string {
  const puntos = puntosDeHebra(opts).map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`)
  return `M${puntos.join('L')}`
}

function prefiereMenosMovimiento(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

// ---------------------------------------------------------------------------
// CordSpine — columna vertebral de la página pública
// ---------------------------------------------------------------------------

export interface CordSpineProps {
  /** Fracción 0..1 de cordón trazado. Si se omite, se ata al scroll de la página. */
  progress?: number
  /** Posiciones 0..1 donde va un nudo (una por sección visible). */
  knots?: number[]
  className?: string
}

export function CordSpine({ progress, knots = [], className }: CordSpineProps) {
  const [scroll, setScroll] = useState(0)
  const atadoAlScroll = progress === undefined

  useEffect(() => {
    if (!atadoAlScroll) return

    let pendiente = false
    const medir = () => {
      pendiente = false
      const alto = document.documentElement.scrollHeight - window.innerHeight
      setScroll(alto <= 0 ? 1 : Math.min(1, Math.max(0, window.scrollY / alto)))
    }
    const alScroll = () => {
      if (pendiente) return
      pendiente = true
      requestAnimationFrame(medir)
    }

    medir()
    window.addEventListener('scroll', alScroll, { passive: true })
    window.addEventListener('resize', alScroll)
    return () => {
      window.removeEventListener('scroll', alScroll)
      window.removeEventListener('resize', alScroll)
    }
  }, [atadoAlScroll])

  const avance = progress ?? scroll
  const trazado = prefiereMenosMovimiento() ? 1 : avance

  const LARGO = 1000
  const ANCHO = 14
  const paths = useMemo(
    () => FASES.map((fase) => hebra({ largo: LARGO, ancho: ANCHO, vueltas: 9, fase, vertical: true })),
    [],
  )

  return (
    <svg
      aria-hidden="true"
      viewBox={`0 0 ${ANCHO} ${LARGO}`}
      preserveAspectRatio="none"
      className={className}
    >
      {paths.map((d, i) => (
        <path
          key={i}
          d={d}
          className="cordon-hebra"
          stroke="currentColor"
          opacity={0.35 + i * 0.2}
          style={{
            strokeDasharray: LARGO * 1.15,
            strokeDashoffset: LARGO * 1.15 * (1 - trazado),
            transition: 'stroke-dashoffset 120ms linear',
          }}
        />
      ))}

      {knots.map((posicion, i) => (
        <circle
          key={i}
          cx={ANCHO / 2}
          cy={LARGO * posicion}
          r={trazado >= posicion ? 3.2 : 0}
          fill="currentColor"
          className="cordon-nudo"
        />
      ))}
    </svg>
  )
}

// ---------------------------------------------------------------------------
// CordBraid — progreso de la guía de planificación
// ---------------------------------------------------------------------------

export interface CordBraidProps {
  /** Una fracción 0..1 por hebra. Se esperan tres: las tres secciones de la guía. */
  strands: number[]
  className?: string
}

/**
 * Tres hebras horizontales, cada una avanzando con su sección. Cuando las tres
 * llegan al final, el cordón está completo — que es exactamente lo que la
 * pareja quiere ver el día que termina de planificar.
 */
export function CordBraid({ strands, className }: CordBraidProps) {
  const LARGO = 300
  const ALTO = 18

  const paths = useMemo(
    () =>
      FASES.map((fase) =>
        hebra({ largo: LARGO, ancho: ALTO, vueltas: 4, fase, vertical: false, pasos: 90 }),
      ),
    [],
  )

  return (
    <svg
      aria-hidden="true"
      viewBox={`0 0 ${LARGO} ${ALTO}`}
      preserveAspectRatio="none"
      className={className}
    >
      {paths.map((d, i) => (
        <g key={i}>
          <path d={d} className="cordon-hebra" stroke="currentColor" opacity={0.16} />
          <path
            d={d}
            className="cordon-hebra"
            stroke="currentColor"
            opacity={0.45 + i * 0.2}
            style={{
              strokeDasharray: LARGO * 1.1,
              strokeDashoffset: LARGO * 1.1 * (1 - Math.min(1, Math.max(0, strands[i] ?? 0))),
              transition: 'stroke-dashoffset 700ms cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          />
        </g>
      ))}
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Marca de la plataforma: el cordón reducido a un glifo
// ---------------------------------------------------------------------------

export function CordMark({ className }: { className?: string }) {
  const paths = useMemo(
    () => FASES.map((fase) => hebra({ largo: 26, ancho: 12, vueltas: 1.5, fase, vertical: false, pasos: 40 })),
    [],
  )

  return (
    <svg aria-hidden="true" viewBox="0 0 26 12" className={className}>
      {paths.map((d, i) => (
        <path key={i} d={d} className="cordon-hebra" stroke="currentColor" opacity={0.5 + i * 0.25} />
      ))}
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Revelado progresivo de secciones
// ---------------------------------------------------------------------------

/** Marca el elemento como visible la primera vez que entra en pantalla. */
export function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null)

  useEffect(() => {
    const nodo = ref.current
    if (!nodo) return

    if (prefiereMenosMovimiento()) {
      nodo.dataset.visible = 'true'
      return
    }

    const observador = new IntersectionObserver(
      (entradas) => {
        for (const entrada of entradas) {
          if (entrada.isIntersecting) {
            nodo.dataset.visible = 'true'
            observador.unobserve(nodo)
          }
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.1 },
    )

    observador.observe(nodo)
    return () => observador.disconnect()
  }, [])

  return ref
}
